<template>
  <form class="signature-form" @submit.prevent="handleSubmit">
    <div class="form-grid">
      <label class="form-field">
        <span>Nome do assinante</span>
        <input
          v-model.trim="form.signer.name"
          type="text"
          placeholder="Ex: Alexandre dos Santos Dias"
          required
        />
      </label>

      <label class="form-field">
        <span>Cargo/Função</span>
        <input
          v-model.trim="form.signer.role"
          type="text"
          placeholder="Ex: Instrutor de treinamentos"
        />
      </label>

      <label class="form-field">
        <span>Registro profissional</span>
        <input
          v-model.trim="form.signer.registry"
          type="text"
          placeholder="Ex: Reg. M.T.E.: SP011576.2"
        />
      </label>

      <label class="form-field">
        <span>E-mail</span>
        <input
          v-model.trim="form.signer.email"
          type="email"
          placeholder="email@empresa.com.br"
        />
      </label>
    </div>

    <div class="placement-card">
      <div class="placement-header">
        <div>
          <strong>Posição padrão no certificado</strong>
          <small
            >Esses valores serão usados futuramente na renderização
            dinâmica.</small
          >
        </div>
      </div>

      <div class="form-grid placement-grid">
        <label class="form-field">
          <span>Modelo de posição</span>
          <select v-model="selectedPositionKey" @change="applySelectedPosition">
            <option
              v-for="position in signaturePositions"
              :key="position.key"
              :value="position.key"
            >
              {{ position.label }}
            </option>
          </select>
        </label>

        <label class="form-field">
          <span>Página</span>
          <select v-model="form.placement.page">
            <option value="front">Frente</option>
            <option value="back">Verso</option>
          </select>
        </label>

        <label class="form-field">
          <span>Topo (mm)</span>
          <input
            v-model.number="form.placement.topMm"
            type="number"
            step="0.1"
          />
        </label>

        <label class="form-field">
          <span>Esquerda (mm)</span>
          <input
            v-model.number="form.placement.leftMm"
            type="number"
            step="0.1"
          />
        </label>

        <label class="form-field">
          <span>Largura (mm)</span>
          <input
            v-model.number="form.placement.widthMm"
            type="number"
            step="0.1"
          />
        </label>

        <label class="form-field">
          <span>Altura (mm)</span>
          <input
            v-model.number="form.placement.heightMm"
            type="number"
            step="0.1"
          />
        </label>
      </div>
    </div>

    <div class="pad-card">
      <div class="pad-header">
        <div>
          <strong>Desenhe a assinatura</strong>
          <small>Salvaremos em PNG transparente, sem fundo.</small>
        </div>

        <button type="button" class="ghost-button" @click="clearPad">
          Limpar
        </button>
      </div>

      <div class="canvas-wrap">
        <canvas ref="canvasRef"></canvas>
      </div>

      <p class="pad-hint">
        Use mouse, caneta digital ou toque na tela. Para melhor resultado,
        assine de forma centralizada.
      </p>
    </div>

    <div v-if="previewDataUrl" class="preview-card">
      <span>Prévia recortada</span>
      <div class="preview-box">
        <img :src="previewDataUrl" alt="Prévia da assinatura" />
      </div>
    </div>

    <div class="actions">
      <button type="button" class="secondary-button" @click="$emit('cancel')">
        Cancelar
      </button>

      <button type="submit" class="primary-button">
        {{ editingSignature ? "Atualizar assinatura" : "Salvar assinatura" }}
      </button>
    </div>
  </form>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import SignaturePad from "signature_pad";
import {
  signaturePositions,
  getDefaultSignaturePosition,
  findSignaturePosition,
} from "@/lookups/signaturePositions";

const props = defineProps({
  editingSignature: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["saved", "cancel"]);

const canvasRef = ref(null);
const signaturePad = ref(null);
const previewDataUrl = ref(null);
const selectedPositionKey = ref(getDefaultSignaturePosition().key);

const form = reactive(createEmptyForm());

const isEditing = computed(() => Boolean(props.editingSignature));

function createEmptyForm() {
  const defaultPosition = getDefaultSignaturePosition();

  return {
    id: null,
    schemaVersion: 1,
    type: "visual",
    status: "active",

    signer: {
      name: "",
      role: "",
      registry: "",
      document: "",
      email: "",
    },

    image: {
      mime: "image/png",
      dataUrl: "",
      width: 0,
      height: 0,
      transparent: true,
    },

    placement: {
      page: defaultPosition.page,
      topMm: defaultPosition.topMm,
      leftMm: defaultPosition.leftMm,
      widthMm: defaultPosition.widthMm,
      heightMm: defaultPosition.heightMm,
      zIndex: defaultPosition.zIndex,
    },

    digital: {
      enabled: false,
      provider: null,
      certificateAlias: null,
      validationMode: null,
    },

    createdAt: null,
    updatedAt: null,
  };
}

function resetForm() {
  const empty = createEmptyForm();

  Object.assign(form, empty);
  selectedPositionKey.value = getDefaultSignaturePosition().key;
  previewDataUrl.value = null;

  clearPad();
}

function fillForm(signature) {
  if (!signature) {
    resetForm();
    return;
  }

  Object.assign(form, createEmptyForm(), signature);

  const matchedPosition = signaturePositions.find((position) => {
    return (
      position.page === signature.placement?.page &&
      Number(position.topMm) === Number(signature.placement?.topMm) &&
      Number(position.leftMm) === Number(signature.placement?.leftMm)
    );
  });

  selectedPositionKey.value =
    matchedPosition?.key || getDefaultSignaturePosition().key;
  previewDataUrl.value = signature.image?.dataUrl || null;

  nextTick(() => {
    clearPad();

    if (signature.image?.dataUrl && signaturePad.value) {
      signaturePad.value.fromDataURL(signature.image.dataUrl);
    }
  });
}

function applySelectedPosition() {
  const position = findSignaturePosition(selectedPositionKey.value);

  if (!position) return;

  form.placement.page = position.page;
  form.placement.topMm = position.topMm;
  form.placement.leftMm = position.leftMm;
  form.placement.widthMm = position.widthMm;
  form.placement.heightMm = position.heightMm;
  form.placement.zIndex = position.zIndex;
}

function resizeCanvas() {
  const canvas = canvasRef.value;

  if (!canvas) return;

  const ratio = Math.max(window.devicePixelRatio || 1, 1);
  const width = canvas.offsetWidth;
  const height = canvas.offsetHeight;

  canvas.width = width * ratio;
  canvas.height = height * ratio;

  const context = canvas.getContext("2d");
  context.scale(ratio, ratio);

  if (signaturePad.value) {
    signaturePad.value.clear();
  }
}

function setupSignaturePad() {
  const canvas = canvasRef.value;

  if (!canvas) return;

  resizeCanvas();

  signaturePad.value = new SignaturePad(canvas, {
    minWidth: 0.8,
    maxWidth: 2.4,
    throttle: 16,
    backgroundColor: "rgba(0, 0, 0, 0)",
    penColor: "#0f172a",
  });
}

function clearPad() {
  if (signaturePad.value) {
    signaturePad.value.clear();
  }
}

function trimCanvas(sourceCanvas) {
  const context = sourceCanvas.getContext("2d");
  const { width, height } = sourceCanvas;
  const imageData = context.getImageData(0, 0, width, height);
  const data = imageData.data;

  let top = null;
  let left = null;
  let right = null;
  let bottom = null;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = data[(y * width + x) * 4 + 3];

      if (alpha > 0) {
        if (top === null) top = y;
        if (left === null || x < left) left = x;
        if (right === null || x > right) right = x;
        if (bottom === null || y > bottom) bottom = y;
      }
    }
  }

  if (top === null) {
    return null;
  }

  const padding = 16;
  const safeLeft = Math.max(left - padding, 0);
  const safeTop = Math.max(top - padding, 0);
  const safeRight = Math.min(right + padding, width);
  const safeBottom = Math.min(bottom + padding, height);

  const trimmedWidth = safeRight - safeLeft;
  const trimmedHeight = safeBottom - safeTop;

  const trimmedCanvas = document.createElement("canvas");
  trimmedCanvas.width = trimmedWidth;
  trimmedCanvas.height = trimmedHeight;

  const trimmedContext = trimmedCanvas.getContext("2d");

  trimmedContext.putImageData(
    context.getImageData(safeLeft, safeTop, trimmedWidth, trimmedHeight),
    0,
    0
  );

  return trimmedCanvas;
}

function handleSubmit() {
  if (!form.signer.name) {
    alert("Informe o nome do assinante.");
    return;
  }

  const hasNewDraw = signaturePad.value && !signaturePad.value.isEmpty();

  if (!hasNewDraw && !form.image?.dataUrl) {
    alert("Desenhe a assinatura antes de salvar.");
    return;
  }

  let imagePayload = form.image;

  if (hasNewDraw) {
    const trimmedCanvas = trimCanvas(canvasRef.value);

    if (!trimmedCanvas) {
      alert("Não foi possível identificar a assinatura desenhada.");
      return;
    }

    const dataUrl = trimmedCanvas.toDataURL("image/png");

    imagePayload = {
      mime: "image/png",
      dataUrl,
      width: trimmedCanvas.width,
      height: trimmedCanvas.height,
      transparent: true,
    };

    previewDataUrl.value = dataUrl;
  }

  emit("saved", {
    ...form,
    image: imagePayload,
  });
}

watch(
  () => props.editingSignature,
  (signature) => {
    fillForm(signature);
  },
  { immediate: true }
);

onMounted(() => {
  setupSignaturePad();
  window.addEventListener("resize", resizeCanvas);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeCanvas);

  if (signaturePad.value) {
    signaturePad.value.off();
  }
});
</script>

<style scoped>
.signature-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.42rem;
  text-align: left;
}

.form-field span {
  color: #334155;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.form-field input,
.form-field select {
  width: 100%;
  border: 1px solid #d6deea;
  border-radius: 14px;
  padding: 0.78rem 0.85rem;
  color: #111827;
  background: #ffffff;
  outline: none;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);
  transition: 0.2s ease;
}

.form-field input:focus,
.form-field select:focus {
  border-color: rgba(255, 122, 0, 0.72);
  background: #fffaf3;
  box-shadow: 0 0 0 4px rgba(255, 122, 0, 0.12),
    0 14px 30px rgba(15, 23, 42, 0.08);
}

.placement-card,
.pad-card,
.preview-card {
  border: 1px solid rgba(214, 222, 234, 0.95);
  border-radius: 22px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.05);
}

.placement-header,
.pad-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
  text-align: left;
}

.placement-header strong,
.pad-header strong {
  display: block;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.95rem;
}

.placement-header small,
.pad-header small,
.pad-hint {
  display: block;
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.76rem;
  line-height: 1.6;
}

.placement-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.canvas-wrap {
  overflow: hidden;
  border: 1px dashed rgba(255, 122, 0, 0.5);
  border-radius: 18px;
  background-image: linear-gradient(
      45deg,
      rgba(15, 23, 42, 0.05) 25%,
      transparent 25%
    ),
    linear-gradient(-45deg, rgba(15, 23, 42, 0.05) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, rgba(15, 23, 42, 0.05) 75%),
    linear-gradient(-45deg, transparent 75%, rgba(15, 23, 42, 0.05) 75%);
  background-position: 0 0, 0 10px, 10px -10px, -10px 0;
  background-size: 20px 20px;
  background-color: #ffffff;
}

canvas {
  display: block;
  width: 100%;
  height: 210px;
  touch-action: none;
}

.preview-card {
  text-align: left;
}

.preview-card > span {
  display: block;
  margin-bottom: 0.7rem;
  color: #334155;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.preview-box {
  display: grid;
  place-items: center;
  min-height: 105px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #ffffff;
}

.preview-box img {
  max-width: 280px;
  max-height: 86px;
  object-fit: contain;
}

.actions {
  position: sticky;
  bottom: -1.25rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin: 0 -1.25rem -1.25rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid rgba(214, 222, 234, 0.95);
  background: rgba(248, 250, 252, 0.92);
  backdrop-filter: blur(14px);
}

.primary-button,
.secondary-button,
.ghost-button {
  border: 0;
  border-radius: 14px;
  padding: 0.78rem 1rem;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.74rem;
  cursor: pointer;
  transition: 0.2s ease;
}

.primary-button {
  color: #111827;
  background: linear-gradient(135deg, #ffb020, #ff7a00);
  box-shadow: 0 12px 26px rgba(255, 122, 0, 0.22);
}

.secondary-button {
  color: #334155;
  background: #ffffff;
  border: 1px solid #d6deea;
}

.ghost-button {
  color: #b45309;
  background: #fff0dc;
}

.primary-button:hover,
.secondary-button:hover,
.ghost-button:hover {
  transform: translateY(-1px);
}

@media (max-width: 860px) {
  .form-grid,
  .placement-grid {
    grid-template-columns: 1fr;
  }

  .actions {
    flex-direction: column-reverse;
  }

  .actions button {
    width: 100%;
  }
}
</style>
